import { TalerFormAttributes } from "@gnu-taler/taler-util";
import { InternationalizationAPI } from "../../context/translation.js";
import { DoubleColumnFormDesign, FormMetadata } from "../forms-types.js";
import { countryNationalityList } from "../../utils/select-ui-lists.js";
import { intervalToDuration, isFuture, isValid, parse } from "date-fns";
import { vqfLanguagePolicy } from "./vqf.js";

export const form_vqf_902_9_officer = (
  i18n: InternationalizationAPI,
): FormMetadata => ({
  label: i18n.fixed({
    en: "Declaration of identity of the beneficial owner (A) — AML officer",
    de: "Feststellung des wirtschaftlich Berechtigten (A) — GwG-Beauftragter",
  }),
  description: i18n.fixed({
    en: "The AML officer records the declaration and uploads the customer's signed PDF.",
    de: "Der GwG-Beauftragte erfasst die Erklärung und lädt das vom Kunden unterzeichnete PDF hoch.",
  }),
  id: "vqf_902_9_officer",
  version: 1,
  config: VQF_902_9_officer(i18n),
});

export function VQF_902_9_officer(
  i18n: InternationalizationAPI,
): DoubleColumnFormDesign {
  return {
    type: "double-column",
    languagePolicy: vqfLanguagePolicy(i18n),
    title: i18n.fixed({
      en: "Declaration of identity of the beneficial owner",
      de: "Feststellung des wirtschaftlich Berechtigten",
    }),
    sections: [
      {
        title: i18n.fixed({
          en: "Identity of the contracting partner",
          de: "Identität des Vertragspartners",
        }),
        fields: [
          {
            id: TalerFormAttributes.IDENTITY_CONTRACTING_PARTNER,
            label: i18n.fixed({
              en: "Contracting partner",
              de: "Vertragspartner",
            }),
            type: "textArea",
            required: true,
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Beneficial owner details",
          de: "Angaben zum wirtschaftlich Berechtigten",
        }),
        fields: [
          {
            id: TalerFormAttributes.IDENTITY_LIST,
            label: i18n.fixed({
              en: "Beneficial owner(s)",
              de: "Wirtschaftlich Berechtigte(r)",
            }),
            help: i18n.fixed({
              en: "The person(s) listed below is/are the beneficial owner(s) of the assets involved in the business relationship. If the contracting partner is also the sole beneficial owner of the assets, the contracting partner's detail must be set out below",
              de: "Die nachfolgend aufgeführte(n) Person(en) ist/sind an den in die Geschäftsbeziehung eingebrachten Vermögenswerten wirtschaftlich berechtigt. Ist der Vertragspartner selber allein an diesen Vermögenswerten wirtschaftlich berechtigt, so sind nachstehend seine Personalien festzuhalten",
            }),
            type: "array",
            validator(persons) {
              if (!persons || persons.length < 1) {
                return i18n.fixed({
                  en: "Can't be empty",
                  de: "Darf nicht leer sein",
                });
              }
              return undefined;
            },
            labelFieldId: TalerFormAttributes.FULL_NAME,
            fields: [
              {
                id: TalerFormAttributes.FULL_NAME,
                label: i18n.fixed({
                  en: "Full name",
                  de: "Vollständiger Name",
                }),
                type: "text",
                required: true,
              },
              {
                id: TalerFormAttributes.DOMICILE_ADDRESS,
                label: i18n.fixed({
                  en: "Domicile address",
                  de: "Sitzadresse",
                }),
                type: "textArea",
                required: true,
              },
              {
                id: TalerFormAttributes.DATE_OF_BIRTH,
                label: i18n.fixed({ en: "Date of birth", de: "Geburtsdatum" }),
                type: "isoDateText",
                placeholder: "dd/MM/yyyy",
                pattern: "dd/MM/yyyy",
                required: true,
                validator(text, form) {
                  //FIXME: why returning in this format even if pattern is in another?
                  const time = parse(text, "yyyy-MM-dd", new Date());
                  if (!isValid(time)) {
                    return i18n.fixed({
                      en: "invalid format",
                      de: "ungültiges Format",
                    });
                  }
                  if (isFuture(time)) {
                    return i18n.fixed({
                      en: "it can't be in the future",
                      de: "darf nicht in der Zukunft liegen",
                    });
                  }
                  const { years } = intervalToDuration({
                    start: time,
                    end: new Date(),
                  });
                  if (years && years > 120) {
                    return i18n.fixed({
                      en: "it can't be greater than 120 years",
                      de: "darf nicht größer als 120 Jahre sein",
                    });
                  }
                  return undefined;
                },
              },
              {
                id: TalerFormAttributes.NATIONALITY,
                label: i18n.fixed({
                  en: "Nationality",
                  de: "Staatsangehörigkeit",
                }),
                type: "selectOne",
                choices: countryNationalityList(i18n),
                preferredChoiceVals: ["CH"],
                required: true,
              },
            ],
            required: false,
          },
        ],
      },
      {
        title: i18n.fixed({
          en: "Signed Declaration",
          de: "Unterzeichnete Erklärung",
        }),
        description: i18n.fixed({
          en: "Signed declaration by the customer",
          de: "Vom Kunden unterzeichnete Erklärung",
        }),
        fields: [
          {
            type: "caption",
            label: i18n.fixed({
              en: "The uploaded document must contain the customer's signature on the beneficial owner declaration.",
              de: "Das hochgeladene Dokument muss die Unterschrift des Kunden auf der Erklärung zum wirtschaftlich Berechtigten enthalten.",
            }),
          },
          {
            id: TalerFormAttributes.ATTACHMENT_SIGNED_DOCUMENT,
            label: i18n.fixed({
              en: "Signed Document",
              de: "Unterzeichnetes Dokument",
            }),
            type: "file",
            accept: "application/pdf",
            required: true,
          },
        ],
      },
    ],
  };
}
